import { merge, noop } from "lodash";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Subject, timer } from "rxjs";
import { filter, scan, switchMap } from "rxjs/operators";
import styled from "styled-components";
import { object, string } from "yup";
import { captcha } from "../../../../components/captcha";
import { TextField } from "../../../../components/forms/text";
import { useForm } from "../../../../components/hooks/form/form";
import { Icon } from "../../../../components/icons";
import { columnFlex } from "../../../../components/layout";
import { useAppLoader } from "../../../../components/loader/app-loader-hook";
import { StepperContext } from "../../../../components/stepper/context";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { Field } from "../../../../store/launch-configurations/models/internal/field";
import { LaunchesDTO } from "../../../../store/launches/models/dto";
import { ParticipationsService } from "../../../../store/participations/service";
import { Container, FormPrimaryButton } from "../components";
import { ConfigContext } from "../config-context";
import { useDataInRedux } from "../persistant-form-storage";
import { useStepForm } from "../step/step-form.hook";

type FormType = LaunchesDTO.Requests.ParticipationForm.ForForm;

const StyledContainer = styled(Container)`
  ${columnFlex};
  align-items: center;
  text-align: center;
`;

const EmailIcon = styled(Icon)`
  font-size: 4em;
`;

const textFieldWidth = 30;
const StyledTextField = styled(TextField)`
  margin: 2em 0;
  width: ${textFieldWidth}em;
  max-width: 100%;
`;

export interface IEmailVerificationPageProps {
  item: LaunchConfigurationsDTO.Responses.GetSingle.T;
}

type IInnerEmailVerificationPageProps<
  T extends keyof FormType
> = IEmailVerificationPageProps & {
  defaultValues: Pick<FormType, T>;
  persistInStorage: (x: Pick<FormType, T>) => void;
};

const fontSize = 0.8;
const AlreadyScreened = styled.div`
  font-size: ${fontSize}em;
  font-style: italic;
  margin-bottom: 2em;
  width: ${textFieldWidth * (1 / fontSize)}em;
  max-width: 100%;
`;

const EmailVerificationPage: FC<IEmailVerificationPageProps> = (props) => {
  const { persistInStorage, data } = useDataInRedux<any>(props.item);
  return (
    <InnerEmailVerificationPage
      {...props}
      defaultValues={data as { [x: string]: any }}
      persistInStorage={persistInStorage}
    />
  );
};

const InnerEmailVerificationPage: FC<IInnerEmailVerificationPageProps<any>> = ({
  item,
  defaultValues,
  persistInStorage,
}) => {
  const loader = useAppLoader();
  const { t, i18n } = useTranslation("token-onboarding");

  const {
    state: { emailVerified, setEmailVerified, setUserKycStatus, setUserExists },
  } = useContext(ConfigContext);

  const fields = useMemo<Array<Field.Field<string>>>(
    () => [
      {
        field: "user.emailAddress",
        title: {},
        desc: {},
      },
    ],
    []
  );

  const resultDefaults = useMemo<typeof defaultValues>(
    () =>
      merge(defaultValues, {
        user: {
          emailAddress: emailVerified || defaultValues.user?.emailAddress || "",
        },
      }),
    [defaultValues, emailVerified]
  );

  const { setValidation, setIsNextEnabled, next } = useContext(StepperContext);
  const { handleSubmit, formState, register, watch } = useStepForm({
    item,
    fields,
    isLast: false,
    defaultValues: resultDefaults,
    persistInStorage,
  });

  const currentEmailAddress = watch("user.emailAddress");

  const optFormDefaults = useMemo<{ otp: string }>(
    () => ({
      otp: "",
    }),
    []
  );

  const otpForm = useForm<{ otp: string | undefined }>({
    defaultValues: optFormDefaults,
    validationSchema: useMemo(
      () => object().shape({ otp: string().required() }),
      []
    ),
  });

  const [phase, setPhase] = useState<number>(0);

  const { secondsLeft, setTimer } = useResendTimer();

  useEffect(() => {
    otpForm.handleChange("otp")("");
  }, [phase, emailVerified]);

  useEffect(() => {
    setIsNextEnabled(
      (emailVerified === currentEmailAddress && !!emailVerified) || phase > 0
    );
    return () => {
      setIsNextEnabled(true);
    };
  }, [phase, emailVerified, currentEmailAddress, setIsNextEnabled]);

  const emailDisabled = useMemo(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    return urlSearchParams.has("user");
  }, [window.location.search]);

  const validationCallback = useCallback(async () => {
    if (phase > 0) {
      const code = await captcha();

      try {
        const { status, userExists } = await ParticipationsService.verifyOtp({
          configurationId: item.id,
          emailAddress: currentEmailAddress,
          otpCode: otpForm.values["otp"] as string,
          captcha: code,
          locale: i18n.language.replaceAll("-", "_"),
        });

        if (!item.configuration.allowMultipleSubmissions) {
          await ParticipationsService.checkPreviousParticipation({
            configurationId: item.id,
            emailAddress: currentEmailAddress,
          });
        }

        setEmailVerified(currentEmailAddress);
        setUserKycStatus(status);
        setUserExists(userExists);
      } catch (e: any) {
        if (e.message === "otp-is-incorrect") {
          otpForm.setErrors({
            ...otpForm.errors,
            otp: {
              message: t("otp-is-incorrect", { ns: "errors" }),
              type: "error",
            },
          });
        }
        throw e;
      }
    }
  }, [
    phase,
    item,
    currentEmailAddress,
    otpForm.values,
    i18n.language,
    t,
    otpForm.errors,
  ]);

  useEffect(() => {
    if (emailVerified === undefined) {
      setValidation(() => () =>
        !!emailVerified && emailVerified === currentEmailAddress
          ? Promise.resolve()
          : loader(
            otpForm.handleSubmit(validationCallback, { throwError: true })()
          )
      );
    }
  }, [
    otpForm.handleSubmit,
    validationCallback,
    emailVerified,
    currentEmailAddress,
    loader,
    resultDefaults, // Adding this because otherwise the setValidation in useStepForm will overwrite this
  ]);

  const requestOtpCallback = useCallback(
    (data: Pick<LaunchesDTO.Requests.ParticipationForm.ForForm, any>) =>
      loader(
        (async () => {
          const code = await captcha();
          setEmailVerified(undefined);
          setUserKycStatus(undefined);
          return ParticipationsService.requestOtp({
            configurationId: item.id,
            emailAddress: data?.user?.emailAddress,
            captcha: code,
            locale: i18n.language.replaceAll("-", "_"),
          })
            .then(() => {
              setPhase(1);
              setTimer();
            })
            .catch(noop);
        })()
      ),
    [
      captcha,
      setEmailVerified,
      setUserKycStatus,
      setTimer,
      item,
      setPhase,
      i18n.language,
    ]
  );

  if (phase < 1) {
    return (
      <StyledContainer key={0} onSubmit={handleSubmit(requestOtpCallback)}>
        <h2>{t("please-verify-your-e-mail-address")}</h2>
        <EmailIcon type="emailQuestion" />
        <p>{t("enter-your-e-mail-address-to-receive-a-verification-code")}</p>
        <StyledTextField
          {...register("user.emailAddress")}
          type="email"
          label="E-mail"
          autoComplete="email"
          disabled={emailDisabled}
        />
        <AlreadyScreened>
          {t("if-you-have-already-been-screened-by-sekuritance")}
        </AlreadyScreened>
        <FormPrimaryButton
          type="submit"
          disabled={
            (formState.isSubmitted && !formState.isValid) || secondsLeft > 0
          }
        >
          {secondsLeft
            ? t("send-verification-code-in-x-seconds", { secondsLeft })
            : t("send-verification-code")}
        </FormPrimaryButton>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer
      key={1}
      onSubmit={otpForm.handleSubmit(async () => {
        next.next();
      })}
    >
      <h2>{t("please-verify-your-e-mail-address")}</h2>
      <EmailIcon type="emailTick" />
      <p
        dangerouslySetInnerHTML={{
          __html: t("enter-the-verification-we-sent-to", {
            email: currentEmailAddress,
          }),
        }}
      />
      <StyledTextField {...otpForm.register("otp")} type="text" label="Enter your One Time Password here" />
      <p style={{ marginTop: 0 }}>
        <a onClick={() => setPhase(0)}>{t("didn-t-get-the-code")}</a>
      </p>
    </StyledContainer>
  );
};

export const useEmailPage = () => {
  const { t } = useTranslation("page-titles");
  return {
    title: t("Email"),
    content: EmailVerificationPage,
  };
};

function useResendTimer() {
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const time$ = useMemo(() => new Subject<void>(), []);

  useEffect(() => {
    const sub = time$
      .pipe(
        switchMap(() => timer(0, 1000).pipe(scan((acc) => --acc, 60))), // timer for 1 minute
        filter((x) => x >= 0)
      )
      .subscribe(setSecondsLeft);

    return () => sub.unsubscribe();
  }, [time$, setSecondsLeft]);

  const setTimer = useCallback(() => {
    time$.next();
  }, [time$]);

  return {
    secondsLeft,
    setTimer,
  };
}
